import { QueryClient, DefaultOptions } from '@tanstack/react-query';
import { getErrorMessage, isNetworkError, isServerError } from './api';

// Default query options for React Query
const defaultQueryOptions: DefaultOptions = {
  queries: {
    // Stale time - how long data is considered fresh (5 minutes)
    staleTime: 5 * 60 * 1000,
    // Cache time - how long data stays in cache when not in use (10 minutes)
    cacheTime: 10 * 60 * 1000,
    // Retry configuration
    retry: (failureCount, error) => {
      // Don't retry on client errors (4xx) except network errors
      if (!isNetworkError(error) && !isServerError(error)) {
        return false;
      }
      // Retry up to 3 times with exponential backoff
      return failureCount < 3;
    },
    // Retry delay with exponential backoff
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    // Refetch on window focus for critical data
    refetchOnWindowFocus: false,
    // Refetch on reconnect
    refetchOnReconnect: true,
    // Background refetch interval (disabled by default)
    refetchInterval: false,
  },
  mutations: {
    // Retry mutations only on network/server errors
    retry: (failureCount, error) => {
      if (!isNetworkError(error) && !isServerError(error)) {
        return false;
      }
      return failureCount < 2; // Fewer retries for mutations
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  },
};

// Create the query client with enhanced error handling
export const queryClient = new QueryClient({
  defaultOptions: defaultQueryOptions,
  logger: {
    log: (message) => {
      if (import.meta.env.DEV) {
        console.log(`[React Query] ${message}`);
      }
    },
    warn: (message) => {
      console.warn(`[React Query] ${message}`);
    },
    error: (error) => {
      console.error(`[React Query Error]`, error);
      
      // Log structured error information
      if (error instanceof Error) {
        console.error(`[React Query Error Details]`, {
          message: getErrorMessage(error),
          stack: error.stack,
        });
      }
    },
  },
});

// Query key factory for consistent key generation
export const queryKeys = {
  // Content queries
  content: {
    all: ['content'] as const,
    featured: () => [...queryKeys.content.all, 'featured'] as const,
    movies: (page?: number, filters?: { genre?: string; year?: number; yearFrom?: number; yearTo?: number; includePeople?: boolean }) => 
      [...queryKeys.content.all, 'movies', { page, filters }] as const,
    tvShows: (page?: number, filters?: { genre?: string; year?: number; yearFrom?: number; yearTo?: number; includePeople?: boolean }) => 
      [...queryKeys.content.all, 'tv-shows', { page, filters }] as const,
    popular: (limit?: number, type?: string) => 
      [...queryKeys.content.all, 'popular', { limit, type }] as const,
    details: (id: string) => [...queryKeys.content.all, 'details', id] as const,
    search: (query: string, page?: number) => 
      [...queryKeys.content.all, 'search', { query, page }] as const,
    byGenre: (genre: string, page?: number) => 
      [...queryKeys.content.all, 'by-genre', genre, { page }] as const,
    byYear: (year: number, page?: number) => 
      [...queryKeys.content.all, 'by-year', year, { page }] as const,
    // Enhanced query keys for people data
    moviesWithPeople: (page?: number, filters?: { genre?: string; year?: number; yearFrom?: number; yearTo?: number }) => 
      [...queryKeys.content.all, 'movies', { page, filters, includePeople: true }] as const,
    tvShowsWithPeople: (page?: number, filters?: { genre?: string; year?: number; yearFrom?: number; yearTo?: number }) => 
      [...queryKeys.content.all, 'tv-shows', { page, filters, includePeople: true }] as const,
  },
  // Person queries
  persons: {
    all: ['persons'] as const,
    details: (id: string) => [...queryKeys.persons.all, 'details', id] as const,
  },
  // Genre queries
  genres: {
    all: ['genres'] as const,
    list: () => [...queryKeys.genres.all, 'list'] as const,
  },
} as const;

// Cache invalidation utilities
export const invalidateQueries = {
  // Invalidate all content queries
  allContent: () => queryClient.invalidateQueries({ queryKey: queryKeys.content.all }),
  
  // Invalidate specific content
  contentDetails: (id: string) => 
    queryClient.invalidateQueries({ queryKey: queryKeys.content.details(id) }),
  
  // Invalidate popular content
  popularContent: () => 
    queryClient.invalidateQueries({ 
      queryKey: queryKeys.content.all,
      predicate: (query) => query.queryKey.includes('popular')
    }),
  
  // Invalidate search results
  searchResults: () => 
    queryClient.invalidateQueries({ 
      queryKey: queryKeys.content.all,
      predicate: (query) => query.queryKey.includes('search')
    }),
  
  // Invalidate all person queries
  allPersons: () => queryClient.invalidateQueries({ queryKey: queryKeys.persons.all }),
  
  // Invalidate genres
  genres: () => queryClient.invalidateQueries({ queryKey: queryKeys.genres.all }),
  
  // Enhanced invalidation for people data
  contentWithPeople: () => 
    queryClient.invalidateQueries({
      predicate: (query) => {
        const queryKey = query.queryKey as any[];
        return queryKey.some(key => 
          typeof key === 'object' && key?.includePeople === true
        );
      },
    }),
  
  // Invalidate movies with people data
  moviesWithPeople: () => 
    queryClient.invalidateQueries({
      predicate: (query) => {
        const queryKey = query.queryKey as any[];
        return queryKey.includes('movies') && 
               queryKey.some(key => typeof key === 'object' && key?.includePeople === true);
      },
    }),
  
  // Invalidate TV shows with people data
  tvShowsWithPeople: () => 
    queryClient.invalidateQueries({
      predicate: (query) => {
        const queryKey = query.queryKey as any[];
        return queryKey.includes('tv-shows') && 
               queryKey.some(key => typeof key === 'object' && key?.includePeople === true);
      },
    }),
};

// Prefetch utilities for performance optimization
export const prefetchQueries = {
  // Prefetch featured content
  featuredContent: () => 
    queryClient.prefetchQuery({
      queryKey: queryKeys.content.featured(),
      queryFn: async () => {
        const { contentApi } = await import('./api');
        return contentApi.getFeaturedContent();
      },
    }),
  
  // Prefetch popular content
  popularContent: (limit = 20, type?: 'movie' | 'tvSeries') => 
    queryClient.prefetchQuery({
      queryKey: queryKeys.content.popular(limit, type),
      queryFn: async () => {
        const { contentApi } = await import('./api');
        return contentApi.getTopPopular(limit, type);
      },
    }),
  
  // Prefetch genres for filters
  genres: () => 
    queryClient.prefetchQuery({
      queryKey: queryKeys.genres.list(),
      queryFn: async () => {
        const { contentApi } = await import('./api');
        return contentApi.getGenres();
      },
    }),
  
  // Prefetch content details
  contentDetails: (id: string) => 
    queryClient.prefetchQuery({
      queryKey: queryKeys.content.details(id),
      queryFn: async () => {
        const { contentApi } = await import('./api');
        return contentApi.getContentDetails(id);
      },
    }),
  
  // Enhanced prefetch utilities for people data
  moviesWithPeople: (page = 1, filters?: { genre?: string; year?: number; yearFrom?: number; yearTo?: number }) => 
    queryClient.prefetchQuery({
      queryKey: queryKeys.content.moviesWithPeople(page, filters),
      queryFn: async () => {
        const { contentApi } = await import('./api');
        return contentApi.getMovies(page, 20, { ...filters, includePeople: true });
      },
      staleTime: 10 * 60 * 1000, // 10 minutes for people data
    }),
  
  tvShowsWithPeople: (page = 1, filters?: { genre?: string; year?: number; yearFrom?: number; yearTo?: number }) => 
    queryClient.prefetchQuery({
      queryKey: queryKeys.content.tvShowsWithPeople(page, filters),
      queryFn: async () => {
        const { contentApi } = await import('./api');
        return contentApi.getTVShows(page, 20, { ...filters, includePeople: true });
      },
      staleTime: 10 * 60 * 1000, // 10 minutes for people data
    }),
  
  // Prefetch people data for next page to improve perceived performance
  nextPageWithPeople: (currentPage: number, contentType: 'movies' | 'tvShows', filters?: any) => {
    const nextPage = currentPage + 1;
    const prefetchFn = contentType === 'movies' ? prefetchQueries.moviesWithPeople : prefetchQueries.tvShowsWithPeople;
    prefetchFn(nextPage, filters);
  },
};

export default queryClient;